import { useEffect, useRef, useState } from 'react';

const HeroSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = sectionRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrolled = -rect.top;
      const maxScroll = sectionHeight - windowHeight;
      const p = Math.max(0, Math.min(1, scrolled / maxScroll));
      setProgress(p);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Image zoom: starts at scale 1, zooms to 6x to fill screen
  const imageScale = 1 + progress * 5;
  const borderRadius = Math.max(0, 32 * (1 - progress * 1.2));

  // Overlay gets darker as we zoom
  const overlayOpacity = 0.5 + progress * 0.2;

  // Hero text fades out quickly
  const heroTextOpacity = Math.max(0, 1 - progress * 2.5);
  const heroTextTranslate = progress * -80;

  // Monitor content fades in at the end
  const monitorContentOpacity = Math.max(0, (progress - 0.7) / 0.3);
  const monitorContentTranslate = Math.max(0, 40 - progress * 40);

  // Scroll hint fades out immediately
  const scrollHintOpacity = Math.max(0, 1 - progress * 4);

  return (
    <>
      {/* Scroll-driven zoom section */}
      <div
        ref={sectionRef}
        className="relative bg-black"
        style={{ height: '400vh' }}
      >
        <div className="sticky top-0 h-screen overflow-hidden flex items-center justify-center">

          {/* Zooming image container */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ willChange: 'transform' }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                transform: `scale(${imageScale})`,
                borderRadius: `${borderRadius}px`,
                overflow: 'hidden',
                transition: 'transform 0.05s linear, border-radius 0.05s linear',
                willChange: 'transform',
              }}
            >
              <img
                src="https://public.readdy.ai/ai/img_res/a36977136b66e33932354274c074f353.jpg"
                alt="Bamme-Media Studio"
                className="w-full h-full object-cover object-center"
                style={{ display: 'block' }}
              />
            </div>
          </div>

          {/* Dark overlay */}
          <div
            className="absolute inset-0 z-10 pointer-events-none"
            style={{
              background: `rgba(0,0,0,${overlayOpacity})`,
              transition: 'background 0.05s linear',
            }}
          />

          {/* Hero headline – fades out on scroll */}
          <div
            className="relative z-20 text-center px-6 pointer-events-none"
            style={{
              opacity: heroTextOpacity,
              transform: `translateY(${heroTextTranslate}px)`,
              transition: 'opacity 0.05s linear, transform 0.05s linear',
            }}
          >
            <p className="text-white/50 text-xs uppercase tracking-[0.4em] mb-8 font-light">
              Bamme-Media · Wernigerode
            </p>
            <h1 className="text-5xl md:text-7xl lg:text-9xl font-bold text-white leading-[0.9] tracking-tight mb-8">
              DIGITALE
              <br />
              <span className="text-[#d1bc8a]">EXZELLENZ</span>
            </h1>
            <p className="mt-10 text-base md:text-lg lg:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed font-light">
              Wir kreieren außergewöhnliche digitale Erlebnisse,
              <br className="hidden md:block" />
              die Ihre Marke zum Leben erwecken
            </p>
            <div className="mt-12">
              <a
                href="/kontakt"
                className="inline-flex items-center gap-3 px-6 py-4 md:px-10 md:py-5 text-sm font-medium text-black bg-white hover:bg-[#d1bc8a] transition-all duration-500 group whitespace-nowrap cursor-pointer uppercase tracking-widest"
                style={{ pointerEvents: heroTextOpacity > 0.3 ? 'auto' : 'none' }}
              >
                Projekt starten
                <i className="ri-arrow-right-line text-lg group-hover:translate-x-2 transition-transform duration-300"></i>
              </a>
            </div>
          </div>

          {/* Monitor content – fades in at end of zoom */}
          <div
            className="absolute inset-0 z-30 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
            style={{
              opacity: monitorContentOpacity,
              transform: `translateY(${monitorContentTranslate}px)`,
              transition: 'opacity 0.05s linear, transform 0.05s linear',
            }}
          >
            <p className="text-white/50 text-xs uppercase tracking-[0.4em] mb-8 font-light">
              Bamme-Media · Wernigerode
            </p>
            <h2 className="text-4xl md:text-6xl lg:text-8xl font-bold text-white leading-[0.9] tracking-tight mb-10">
              WIR MACHEN
              <br />
              <span className="text-[#d1bc8a]">DEN UNTERSCHIED</span>
            </h2>
            <p className="text-white/60 text-base md:text-lg lg:text-xl max-w-2xl leading-relaxed font-light mb-12">
              Kreativität trifft Strategie – für digitale Erlebnisse,
              <br className="hidden md:block" />
              die Ihre Marke unvergesslich machen.
            </p>
            <a
              href="#services"
              className="inline-flex items-center gap-3 px-6 py-4 md:px-10 md:py-5 border border-white/30 text-white hover:bg-white hover:text-black transition-all duration-500 text-sm uppercase tracking-widest whitespace-nowrap cursor-pointer"
              style={{ pointerEvents: monitorContentOpacity > 0.5 ? 'auto' : 'none' }}
            >
              Unsere Leistungen
              <i className="ri-arrow-right-line text-lg" />
            </a>
          </div>

          {/* Scroll hint */}
          <div
            className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3 pointer-events-none"
            style={{ opacity: scrollHintOpacity, transition: 'opacity 0.1s linear' }}
          >
            <span className="text-white/40 text-xs uppercase tracking-[0.3em] font-light">Scroll</span>
            <div className="w-px h-16 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
          </div>
        </div>
      </div>

      {/* Marquee Banner – team23tax.at Style */}
      <div className="relative bg-[#f5f3ef] overflow-hidden">
        {/* Top border line */}
        <div className="w-full h-px bg-zinc-200" />

        {/* Inner layout */}
        <div className="max-w-screen-xl mx-auto px-8 py-6 md:py-10 flex items-center justify-between gap-8">
          {/* Left: small label */}
          <div className="flex-shrink-0 flex flex-col items-start gap-1">
          </div>

          {/* Scrolling marquee */}
          <div className="flex-1 overflow-hidden relative">
            {/* Fade edges */}
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#f5f3ef] to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#f5f3ef] to-transparent z-10 pointer-events-none" />

            <div className="flex animate-marquee whitespace-nowrap">
              {[1, 2].map((i) => (
                <div key={i} className="flex items-center gap-8 md:gap-12 px-4 md:px-6">
                  <span className="text-zinc-900 text-sm uppercase tracking-[0.25em] font-semibold">Webdesign</span>
                  <span className="text-[#d1bc8a] text-lg">—</span>
                  <span className="text-zinc-900 text-sm uppercase tracking-[0.25em] font-semibold">Branding</span>
                  <span className="text-[#d1bc8a] text-lg">—</span>
                  <span className="text-zinc-900 text-sm uppercase tracking-[0.25em] font-semibold">Social Media</span>
                  <span className="text-[#d1bc8a] text-lg">—</span>
                  <span className="text-zinc-900 text-sm uppercase tracking-[0.25em] font-semibold">Foto &amp; Video</span>
                  <span className="text-[#d1bc8a] text-lg">—</span>
                  <span className="text-zinc-900 text-sm uppercase tracking-[0.25em] font-semibold">Bundle Pakete</span>
                  <span className="text-[#d1bc8a] text-lg">—</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: CTA link */}
          <div className="flex-shrink-0">
          </div>
        </div>

        {/* Bottom border line */}
        <div className="w-full h-px bg-zinc-200" />
      </div>
    </>
  );
};

export default HeroSection;